<template>
  <div class="about-page">
    
    <section class="about-section">
      <div class="about-container">
        <div class="about-text" data-aos="fade-up">
          <p class="about-paragraph">{{ $t('pages.about.about1') }}</p>
        </div>
      </div>
    </section>

    <section class="amenities-section">
      <div class="section-header">
        <h2 class="section-title">{{ $t('pages.about.amenities.title') }}</h2>
        <div class="section-underline"></div>
      </div>

      <div class="amenities-grid">
        <div 
          v-for="(amenity, index) in amenities" 
          :key="index"
          class="amenity-card"
          data-aos="fade-up"
          :data-aos-delay="index * 100"
        >
          <div class="amenity-icon">{{ amenity.icon }}</div>
          <h3 class="amenity-title">{{ $t(amenity.titleKey) }}</h3>
          <p class="amenity-description">{{ $t(amenity.descKey) }}</p>
        </div>
      </div>
    </section>

    <section class="carousel-section">
      <carousel v-bind="settings" :breakpoints="breakpoints" :autoplay="4000" :wrap-around="true">
        <slide v-for="(image, index) in images" :key="image.id + '-' + index">
          <div class="carousel-image-wrapper">
            <img :src="image.UrlImg" :alt="`Apartmani Sentić Neum - fotografija ${index + 1}`" class="carousel-image" loading="lazy" />
          </div>
        </slide>
        <template #addons>
          <navigation />
        </template>
      </carousel>
    </section>

    <section class="featured-section">
      <div class="section-header">
        <h2 class="section-title">{{ $t('pages.about.featured.title') }}</h2>
        <div class="section-underline"></div>
      </div>

      <div class="apartments-grid">
        <div
          v-for="(apartment, index) in featuredApartments"
          :key="index"
          class="apartment-card"
          data-aos="fade-up"
          :data-aos-delay="index * 100"
          @click="openApartmentModal(apartment)"
        >
          <div class="apartment-image-container">
            <img :src="apartment.image" :alt="`Apartman ${apartment.ime} Neum - pogled na more`" class="apartment-image" loading="lazy" />
            <div class="apartment-overlay">
              <div class="apartment-name">{{ apartment.ime }}</div>
              <div class="view-more">{{ $t('pages.about.featured.viewMore') }}</div>
            </div>
          </div>
          <div class="apartment-content">
            <h3 class="apartment-title">{{ apartment.ime }}</h3>
            <p class="apartment-description">{{ $t(apartment.text) }}</p>
            <div class="apartment-cta">
              <span class="cta-text">{{ $t('pages.about.featured.showGallery') }}</span>
              <span class="cta-arrow">→</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="about-section about-section-alt">
      <div class="about-container">
        <div class="about-text" data-aos="fade-up">
          <p class="about-paragraph">{{ $t('pages.about.about2') }}</p>
        </div>
      </div>
    </section>

    <section class="reviews-section">
      <div class="section-header">
        <h2 class="section-title">{{ $t('pages.about.reviews.title') }}</h2>
        <p class="section-subtitle">{{ $t('pages.about.reviews.subtitle') }}</p>
        <div class="section-underline"></div>
      </div>

      <div class="ratings-container">
        <div class="rating-card" data-aos="fade-up">
          <div class="rating-source">Google</div>
          <div class="rating-score">4.7</div>
          <div class="rating-stars">★★★★★</div>
          <div class="rating-count">245 {{ $t('pages.about.reviews.reviews') }}</div>
        </div>
        <div class="rating-card" data-aos="fade-up" data-aos-delay="100">
          <div class="rating-source">Booking.com</div>
          <div class="rating-score">9.2</div>
          <div class="rating-stars">★★★★★</div>
          <div class="rating-count">{{ $t('pages.about.reviews.exceptional') }}</div>
        </div>
      </div>

      <div class="reviews-grid">
        <div 
          v-for="(review, index) in reviews" 
          :key="index"
          class="review-card"
          data-aos="fade-up"
          :data-aos-delay="index * 100"
        >
          <div class="review-stars">★★★★★</div>
          <p class="review-text">{{ $t(review.textKey) }}</p>
          <div class="review-author">
            <span class="author-name">{{ review.author }}</span>
            <span class="review-source">{{ review.source }}</span>
          </div>
        </div>
      </div>
    </section>

    <section class="carousel-section">
      <carousel v-bind="settings" :breakpoints="breakpoints" :autoplay="4000" :wrap-around="true">
        <slide v-for="(image, index) in images2" :key="image.id + '-' + index">
          <div class="carousel-image-wrapper">
            <img :src="image.UrlImg" :alt="`Apartmani Sentić Neum - fotografija ${index + 1}`" class="carousel-image" loading="lazy" />
          </div>
        </slide>
        <template #addons>
          <navigation />
        </template>
      </carousel>
    </section>

    <section class="faq-section">
      <div class="section-header">
        <h2 class="section-title">{{ $t('pages.about.faq.title') }}</h2>
        <p class="section-subtitle">{{ $t('pages.about.faq.subtitle') }}</p>
        <div class="section-underline"></div>
      </div>

      <div class="faq-container">
        <div 
          v-for="(faq, index) in faqs" 
          :key="index"
          class="faq-item"
          :class="{ 'active': activeFaq === index }"
        >
          <div class="faq-question" @click="toggleFaq(index)">
            <h3>{{ $t(faq.questionKey) }}</h3>
            <span class="faq-icon">{{ activeFaq === index ? '−' : '+' }}</span>
          </div>
          
          <transition name="fade">
            <div v-if="activeFaq === index" class="faq-answer">
              <p>{{ $t(faq.answerKey) }}</p>
            </div>
          </transition>
        </div>
      </div>
    </section>

    <section class="about-section">
      <div class="about-container">
        <div class="about-text" data-aos="fade-up">
          <p class="about-paragraph">{{ $t('pages.about.about3') }}</p>
        </div>
      </div>
    </section>

    <Teleport to="body">
      <div v-if="selectedApartment" class="modal-overlay" @click="closeModal">
        <div class="modal-container" @click.stop>
          <button class="modal-close" @click="closeModal">×</button>
          <div class="modal-header">
            <h3 class="modal-title">{{ selectedApartment.ime }}</h3>
          </div>
          <div class="modal-carousel">
            <carousel v-bind="modalSettings" :wrap-around="true">
              <slide v-for="(image, index) in selectedApartment.images" :key="index">
                <div class="modal-image-wrapper">
                  <img :src="image.UrlImg" :alt="`Apartman ${selectedApartment.ime} Neum - slika ${index + 1}`" class="modal-image" loading="lazy" />
                </div>
              </slide>
              <template #addons>
                <navigation />
                <pagination />
              </template>
            </carousel>
          </div>
        </div>
      </div>
    </Teleport>

  </div>
</template>

<script>
import 'vue3-carousel/dist/carousel.css'
import { Carousel, Slide, Navigation, Pagination } from 'vue3-carousel'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'

export default {
  name: 'AboutComponent',
  components: {
    Carousel,
    Slide,
    Navigation,
    Pagination
  },
  setup() {
    const { t, locale } = useI18n()
    const selectedApartment = ref(null)
    const activeFaq = ref(null)

    const openApartmentModal = (apartment) => {
      selectedApartment.value = apartment
      document.body.style.overflow = 'hidden'
    }

    const closeModal = () => {
      selectedApartment.value = null
      document.body.style.overflow = ''
    }

    const toggleFaq = (index) => {
      activeFaq.value = activeFaq.value === index ? null : index
    }

    return {
      t,
      locale,
      selectedApartment,
      activeFaq,
      openApartmentModal,
      closeModal,
      toggleFaq
    }
  },
  data() {
    return {
      settings: { itemsToShow: 1, snapAlign: 'center' },
      modalSettings: { itemsToShow: 1, snapAlign: 'center' },
      breakpoints: {
        700: { itemsToShow: 2, snapAlign: 'center' },
        1024: { itemsToShow: 3, snapAlign: 'start' },
      },
      amenities: [
        { icon: '🅿️', titleKey: 'pages.about.amenities.parking.title', descKey: 'pages.about.amenities.parking.desc' },
        { icon: '📶', titleKey: 'pages.about.amenities.wifi.title', descKey: 'pages.about.amenities.wifi.desc' },
        { icon: '❄️', titleKey: 'pages.about.amenities.ac.title', descKey: 'pages.about.amenities.ac.desc' },
        { icon: '🕐', titleKey: 'pages.about.amenities.checkin.title', descKey: 'pages.about.amenities.checkin.desc' },
        { icon: '🏖️', titleKey: 'pages.about.amenities.beach.title', descKey: 'pages.about.amenities.beach.desc' },
        { icon: '🍳', titleKey: 'pages.about.amenities.kitchen.title', descKey: 'pages.about.amenities.kitchen.desc' },
      ],
      reviews: [
        { textKey: 'pages.about.reviews.review1', author: 'Marko P.', source: 'Google' },
        { textKey: 'pages.about.reviews.review2', author: 'Ana M.', source: 'Booking.com' },
        { textKey: 'pages.about.reviews.review3', author: 'Igor S.', source: 'Google' },
      ],
      faqs: [
        { questionKey: 'pages.about.faq.q1', answerKey: 'pages.about.faq.a1' },
        { questionKey: 'pages.about.faq.q2', answerKey: 'pages.about.faq.a2' },
        { questionKey: 'pages.about.faq.q3', answerKey: 'pages.about.faq.a3' },
        { questionKey: 'pages.about.faq.q4', answerKey: 'pages.about.faq.a4' },
        { questionKey: 'pages.about.faq.q5', answerKey: 'pages.about.faq.a5' },
      ],
      images: [
        { id: '1', UrlImg: '/images/A6 (8).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/images/A9 (10).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/A6/A6 (12).jpg', alt: 'image-slug' },
        { id: '2', UrlImg: '/images/A7 (5).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/A5/A5 (9).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/images/Sentic1.jpg', alt: 'image-slug' },
        { id: '3', UrlImg: '/images/A8 (9).jpg', alt: 'image-slug' },
        { id: '1', UrlImg: '/images/A9 (11).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/images/A9 (3).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/images/A7 (8).jpg', alt: 'image-slug' },
      ],
      images2: [
        { id: '1', UrlImg: '/images/A7 (11).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/A6/A6 (13).jpg', alt: 'image-slug' },
        { id: '2', UrlImg: '/A5/A5 (2).jpg', alt: 'image-slug' },
        { id: '3', UrlImg: '/images/A7 (3).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/images/Sentic1.jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/A5/A5 (9).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/images/A7 (12).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/images/A9 (8).jpg', alt: 'image-slug' },
        { id: '5', UrlImg: '/images/A5 (8).jpg', alt: 'image-slug' },
      ],
      featuredApartments: [
        {
          ime: "A5",
          text: "pages.gallery.apartmans.textA5",
          image: "/A6/A6 (1).jpg",
          images: [
            { id: '1', UrlImg: '/A6/A6 (1).jpg', alt: 'image-slug' },
            { id: '5', UrlImg: '/A6/A6 (2).jpg', alt: 'image-slug' },
            { id: '2', UrlImg: '/A6/A6 (3).jpg', alt: 'image-slug' },
            { id: '3', UrlImg: '/A6/A6 (4).jpg', alt: 'image-slug' },
            { id: '5', UrlImg: '/A6/A6 (7).jpg', alt: 'image-slug' },
            { id: '3', UrlImg: '/A6/A6 (8).jpg', alt: 'image-slug' },
          ],
        },
        {
          ime: "A9",
          text: "pages.gallery.apartmans.textA9",
          image: "/A9/A9 (4).jpg",
          images: [
            { id: '5', UrlImg: '/A9/A9 (5).jpg', alt: 'image-slug' },
            { id: '5', UrlImg: '/A9/A9 (6).jpg', alt: 'image-slug' },
            { id: '5', UrlImg: '/A9/A9 (7).jpg', alt: 'image-slug' },
            { id: '3', UrlImg: '/A9/A9 (8).jpg', alt: 'image-slug' },
            { id: '5', UrlImg: '/A9/A9 (9).jpg', alt: 'image-slug' },
            { id: '2', UrlImg: '/A9/A9 (3).jpg', alt: 'image-slug' },
          ],
        },
        {
          ime: "A7",
          text: "pages.gallery.apartmans.textA7",
          image: "/A7/A7 (1).jpg",
          images: [
            { id: '1', UrlImg: '/A7/A7 (1).jpg', alt: 'image-slug' },
            { id: '5', UrlImg: '/A7/A7 (2).jpg', alt: 'image-slug' },
            { id: '2', UrlImg: '/A7/A7 (3).jpg', alt: 'image-slug' },
            { id: '3', UrlImg: '/A7/A7 (4).jpg', alt: 'image-slug' },  
            { id: '5', UrlImg: '/A7/A7 (5).jpg', alt: 'image-slug' },
            { id: '5', UrlImg: '/A7/A7 (6).jpg', alt: 'image-slug' },
          ],
        },
      ]
    }
  },
}
</script>

<style scoped>
.about-page { background: var(--color-bg); }

/* About Sections */
.about-section { padding: 100px 60px; background: var(--color-surface); }
.about-section-alt { background: var(--color-bg); }
.about-container { max-width: 900px; margin: 0 auto; }
.about-text { text-align: center; }
.about-paragraph { font-size: 18px; line-height: 1.9; color: var(--color-text); font-weight: 400; letter-spacing: 0.3px; }

/* Section Headers */
.section-header { text-align: center; margin-bottom: 80px; }
.section-title { font-family: var(--font-display); font-size: clamp(34px, 5vw, 48px); font-weight: 600; color: var(--color-primary); margin-bottom: 15px; letter-spacing: -0.5px; }
.section-subtitle { font-size: 17px; color: var(--color-text-muted); margin-bottom: 25px; }
.section-underline { width: 80px; height: 3px; background: linear-gradient(90deg, transparent, var(--color-accent) 20%, var(--color-accent) 80%, transparent); margin: 0 auto; }

/* Amenities Section */
.amenities-section { padding: 100px 60px; background: var(--color-surface); }
.amenities-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 40px; max-width: 1400px; margin: 0 auto; }
.amenity-card { background: var(--color-bg); padding: 40px 30px; border-radius: var(--radius-md); text-align: center; transition: all 0.4s ease; border: 2px solid transparent; }
.amenity-card:hover { transform: translateY(-8px); box-shadow: var(--shadow-hover); border-color: var(--color-primary); background: var(--color-surface); }
.amenity-icon { font-size: 48px; margin-bottom: 20px; }
.amenity-title { font-family: var(--font-display); font-size: 21px; font-weight: 600; color: var(--color-primary); margin-bottom: 12px; }
.amenity-description { font-size: 15px; color: var(--color-text-muted); line-height: 1.7; }

/* Carousel Section */
.carousel-section { padding: 80px 0; background: var(--color-surface); }
.carousel-image-wrapper { padding: 0 10px; cursor: pointer; }
.carousel-image { width: 100%; height: 350px; object-fit: cover; border-radius: var(--radius-md); transition: all 0.4s ease; filter: brightness(0.95); }
.carousel-image:hover { transform: scale(1.03); filter: brightness(1); box-shadow: var(--shadow-hover); }

/* Featured Apartments */
.featured-section { padding: 100px 60px; background: var(--color-bg); }
.apartments-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)); gap: 40px; max-width: 1400px; margin: 0 auto; }
.apartment-card { background: var(--color-surface); border-radius: var(--radius-md); overflow: hidden; cursor: pointer; transition: all 0.4s ease; box-shadow: var(--shadow-soft); }
.apartment-card:hover { transform: translateY(-8px); box-shadow: var(--shadow-hover); }
.apartment-image-container { position: relative; overflow: hidden; height: 280px; }
.apartment-image { width: 100%; height: 100%; object-fit: cover; transition: all 0.6s ease; }
.apartment-card:hover .apartment-image { transform: scale(1.1); }
.apartment-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(to bottom, transparent 0%, rgba(8, 42, 48, 0.8) 100%); display: flex; flex-direction: column; justify-content: flex-end; padding: 30px; }
.apartment-name { font-family: var(--font-display); font-size: 32px; font-weight: 700; color: #ffffff; margin-bottom: 10px; }
.view-more { font-size: 14px; color: #ffffff; letter-spacing: 1px; text-transform: uppercase; }
.apartment-content { padding: 30px; }
.apartment-title { font-family: var(--font-display); font-size: 26px; font-weight: 700; color: var(--color-primary); margin-bottom: 15px; }
.apartment-description { font-size: 15px; line-height: 1.7; color: var(--color-text-muted); margin-bottom: 20px; }
.apartment-cta { display: flex; align-items: center; gap: 10px; font-size: 13px; font-weight: 600; color: var(--color-accent); text-transform: uppercase; }
.cta-arrow { font-size: 18px; transition: transform 0.3s ease; }
.apartment-card:hover .cta-arrow { transform: translateX(5px); }

/* Reviews Section */
.reviews-section { padding: 100px 60px; background: var(--color-surface); }
.ratings-container { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 30px; max-width: 700px; margin: 0 auto 80px; }
.rating-card { background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); padding: 40px 30px; border-radius: var(--radius-md); text-align: center; color: #ffffff; box-shadow: var(--shadow-soft); }
.rating-score { font-family: var(--font-display); font-size: 56px; font-weight: 700; margin-bottom: 10px; }
.rating-stars { font-size: 24px; color: var(--color-gold); margin-bottom: 10px; }
.reviews-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 30px; max-width: 1400px; margin: 0 auto; }
.review-card { background: var(--color-bg); padding: 35px; border-radius: var(--radius-md); border: 2px solid transparent; transition: all 0.4s ease; }
.review-card:hover { transform: translateY(-5px); border-color: var(--color-primary); background: var(--color-surface); }
.review-stars { color: var(--color-gold); font-size: 18px; margin-bottom: 20px; }
.review-text { font-size: 15px; line-height: 1.8; color: var(--color-text); margin-bottom: 20px; font-style: italic; }
.review-author { display: flex; justify-content: space-between; align-items: center; padding-top: 20px; border-top: 1px solid var(--color-border); }
.author-name { font-weight: 600; color: var(--color-primary); }
.review-source { color: var(--color-text-muted); font-size: 13px; }

/* FAQ Section */
.faq-section { padding: 100px 60px; background: var(--color-bg); }
.faq-container { max-width: 900px; margin: 0 auto; }

.faq-item {
  background: var(--color-surface);
  margin-bottom: 15px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  border: 2px solid var(--color-border);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.faq-item.active {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-soft);
}

.faq-question {
  padding: 25px 30px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--color-surface);
}

.faq-question h3 { font-family: var(--font-body); font-size: 18px; font-weight: 600; color: var(--color-text); margin: 0; }
.faq-icon { font-size: 24px; font-weight: bold; color: var(--color-accent); margin-left: 20px; }

.faq-answer {
  padding: 0 30px 25px 30px;
  background: var(--color-surface);
}

.faq-answer p {
  font-size: 16px;
  line-height: 1.8;
  color: var(--color-text-muted);
  margin: 0;
  border-top: 1px solid var(--color-border);
  padding-top: 15px;
}

/* Animacija za vidljivost (Fade) */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Modal */
.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(8, 20, 22, 0.95); z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 20px; }
.modal-container { max-width: 90vw; max-height: 90vh; background: var(--color-surface); border-radius: var(--radius-lg); overflow: hidden; position: relative; }
.modal-close { position: absolute; top: 20px; right: 20px; width: 40px; height: 40px; background: #fff; border: none; border-radius: 50%; font-size: 28px; cursor: pointer; z-index: 10; display: flex; align-items: center; justify-content: center; }
.modal-header { padding: 30px; border-bottom: 1px solid var(--color-border); }
.modal-title { font-family: var(--font-display); font-size: 32px; font-weight: 700; color: var(--color-primary); }
.modal-carousel { padding: 20px; }
.modal-image { max-width: 100%; max-height: 70vh; object-fit: contain; border-radius: var(--radius-sm); }

/* Responsive */
@media (max-width: 1024px) {
  .about-section, .amenities-section, .featured-section, .reviews-section, .faq-section { padding: 80px 40px; }
}

@media (max-width: 768px) {
  .about-section, .amenities-section, .featured-section, .reviews-section, .faq-section { padding: 60px 24px; }
  .apartments-grid, .amenities-grid, .reviews-grid { grid-template-columns: 1fr; }
  .section-header { margin-bottom: 50px; }
  .ratings-container { margin-bottom: 50px; }
  .apartment-name { font-size: 26px; }
}

@media (max-width: 480px) {
  .about-paragraph { font-size: 16px; }
  .faq-question { padding: 20px; }
  .faq-answer { padding: 0 20px 20px; }
}
</style>